import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Star, Clock, Bookmark, BookmarkCheck, Award, Tv, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, isInWatchlist, toggleWatchlist } from "@/lib/api";
import { getMe, beginSocialPulseLogin } from "@/lib/socialpulseOidc";
import { MovieCard, MoviePoster } from "@/components/MovieCard";
import { RelatedReading } from "@/components/RelatedReading";
import { PageToolbar, usePageTranslation } from "@/components/PageToolbar";

export default function MovieDetail() {
  const { id } = useParams();
  const [movie, setMovie] = useState(null);
  const [saved, setSaved] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setMovie(null);
    api.get(`/movies/${id}`).then((r) => {
      setMovie(r.data);
      setSaved(isInWatchlist(id));
    });
    getMe().then(setMe);
  }, [id]);

  const rate = async (score) => {
    try {
      const r = await api.post(`/movies/${id}/rate`, { score });
      setMovie((m) => ({ ...m, community: r.data }));
      toast.success(`You rated ${movie.title} ${score}/10`);
    } catch {
      toast.error("Could not save your rating");
    }
  };

  const blocks = useMemo(
    () => (movie ? { synopsis: movie.synopsis, themes: (movie.themes || []).join(" · ") } : null),
    [movie]
  );
  const { lang, tr, busy } = usePageTranslation(blocks, `movie-${id}`);

  if (!movie) return <main className="max-w-7xl mx-auto px-6 py-24 text-center font-display text-2xl text-[#6b5d51]">Loading…</main>;

  const onToggle = () => {
    const added = toggleWatchlist(movie);
    setSaved(added);
    toast(added ? `Added "${movie.title}" to your watchlist` : `Removed "${movie.title}" from your watchlist`);
  };

  const speakText = `${movie.title}, ${movie.year}, ${movie.country}. Directed by ${movie.director}. ${tr("synopsis", movie.synopsis)}`;

  return (
    <main data-testid="movie-detail-page">
      <section className="bg-[#171210] text-[#f6f1e7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid md:grid-cols-[280px_1fr] gap-10">
          <div className="aspect-[3/4] w-full max-w-[280px] relative overflow-hidden">
            <MoviePoster movie={movie} />
          </div>
          <div>
            <p className="eyebrow" style={{ color: "#e8b96a" }}>
              {movie.type} · {movie.country} · {movie.year}
            </p>
            <h1 data-testid="movie-title" className="font-display font-bold text-4xl sm:text-5xl mt-2">{movie.title}</h1>
            {movie.original_title !== movie.title && (
              <p className="font-script text-2xl text-[#d6cebf] mt-1">{movie.original_title}</p>
            )}
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-[#d6cebf]">
              <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-[#e8b96a] fill-[#e8b96a]" /> {movie.rating?.toFixed(1)} / 10 <span className="text-[#a99e8d]">Editorial</span></span>
              {movie.community?.count > 0 && (
                <span data-testid="community-score" className="flex items-center gap-1.5">
                  <Star className="w-4 h-4 text-[#c94b4b] fill-[#c94b4b]" /> {movie.community.avg} / 10{" "}
                  <span className="text-[#a99e8d]">Community ({movie.community.count})</span>
                </span>
              )}
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {movie.runtime} min</span>
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {movie.language}</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {movie.genres.map((g) => (
                <Link key={g} to={`/explore?genre=${encodeURIComponent(g)}`} className="border border-[#5a4c3e] rounded-full px-3 py-1 text-xs hover:border-[#e8b96a] hover:text-[#e8b96a] transition-colors">
                  {g}
                </Link>
              ))}
            </div>
            <p data-testid="movie-synopsis" className="mt-6 text-lg leading-relaxed text-[#e9e2d4] max-w-2xl">{tr("synopsis", movie.synopsis)}</p>
            <p className="mt-3 text-sm text-[#a99e8d]">Themes: {tr("themes", (movie.themes || []).join(" · "))}</p>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <button
                data-testid="movie-watchlist-button"
                onClick={onToggle}
                className={`inline-flex items-center gap-2 px-5 py-2.5 font-medium transition-colors ${saved ? "bg-[#e8b96a] text-[#171210]" : "bg-[#7d1a1a] text-[#f6f1e7] hover:bg-[#a32222]"}`}
              >
                {saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                {saved ? "On your Watchlist" : "Add to Watchlist"}
              </button>
              <PageToolbar speakText={speakText} busy={busy} lang={lang} />
            </div>
            <div className="mt-5">
              {me ? (
                <div data-testid="rating-widget" className="flex flex-wrap items-center gap-2">
                  <span className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#a99e8d] mr-1">Your rating</span>
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((s) => (
                    <button
                      key={s}
                      data-testid={`rate-${s}`}
                      onClick={() => rate(s)}
                      className={`w-8 h-8 text-sm font-mono-label border transition-colors ${
                        movie.community?.my_score === s
                          ? "bg-[#e8b96a] text-[#171210] border-[#e8b96a]"
                          : "border-[#5a4c3e] text-[#d6cebf] hover:border-[#e8b96a] hover:text-[#e8b96a]"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              ) : (
                <button data-testid="rate-signin-prompt" onClick={() => beginSocialPulseLogin().then((ok) => !ok && toast("SocialPulse sign-in isn't switched on yet"))} className="text-sm text-[#a99e8d] underline hover:text-[#e8b96a] transition-colors">
                  Sign in with SocialPulse to rate this film
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          <div>
            <h2 className="font-display font-bold text-2xl mb-4 rule-thick pt-4">Credits</h2>
            <div className="space-y-3">
              <p><span className="font-mono-label text-xs tracking-[0.2em] uppercase text-[#8a7c6d] mr-3">Director</span> <span className="font-medium">{movie.director}</span></p>
              <p><span className="font-mono-label text-xs tracking-[0.2em] uppercase text-[#8a7c6d] mr-3">Cast</span> {movie.cast.join(", ")}</p>
            </div>
            {movie.linked_people?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {movie.linked_people.map((p) => (
                  <Link key={p.id} to={`/person/${p.id}`} data-testid={`linked-person-${p.id}`} className="bg-[#fbf8f1] border border-[#d8ccb8] rounded-full px-3 py-1.5 text-sm hover:border-[#7d1a1a] hover:text-[#7d1a1a] transition-colors">
                    {p.name} · {p.roles[0]}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {movie.awards?.length > 0 && (
            <div>
              <h2 className="font-display font-bold text-2xl mb-4 rule-thick pt-4">Awards & Honors</h2>
              <ul className="space-y-2">
                {movie.awards.map((a) => (
                  <li key={a} className="flex items-start gap-2 text-[#4a3f35]">
                    <Award className="w-4 h-4 text-[#b06810] mt-1 shrink-0" /> {a}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h2 className="font-display font-bold text-2xl mb-4 rule-thick pt-4">Where to Watch</h2>
            {movie.watch?.map((w, i) => (
              <div key={i} className="flex items-start gap-3 bg-[#fbf8f1] border border-[#d8ccb8] p-4">
                <Tv className="w-5 h-5 text-[#7d1a1a] mt-0.5" />
                <div>
                  <p className="font-medium">{w.provider}</p>
                  <p className="text-sm text-[#6b5d51]">{w.model} · Territory: {w.territory}</p>
                </div>
              </div>
            ))}
            <p className="font-mono-label text-[10px] tracking-wide text-[#8a7c6d] mt-2">Availability varies by territory. Always use lawful viewing options.</p>
          </div>
        </div>

        <aside>
          <h3 className="font-display font-bold text-xl mb-4 rule-thick pt-4">From {movie.country}</h3>
          <Link to={`/country/${movie.country_code}`} data-testid="movie-country-link" className="block bg-[#7d1a1a] text-[#f6f1e7] p-5 hover:bg-[#5c1220] transition-colors">
            <p className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[#e8b96a]">Cinema Home</p>
            <p className="font-display font-bold text-2xl mt-1">{movie.country} →</p>
          </Link>
          <RelatedReading articles={movie.related_articles} className="mt-8" />
        </aside>
      </section>

      {movie.related?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-12">
          <h2 className="font-display font-bold text-2xl mb-6 rule-thick pt-4">Related Films</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {movie.related.map((m) => <MovieCard key={m.id} movie={m} />)}
          </div>
        </section>
      )}
    </main>
  );
}
